import React, { useEffect, useRef } from 'react';
import styled from '@emotion/styled';
import { useQuery } from '@tanstack/react-query';
import { adminApi } from '../../services/adminApi';
import Chart from 'chart.js/auto';
import { GlassCard } from './AdminStyles';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 30px;
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 24px;
`;

const StatCard = styled(GlassCard)<{ gradient: string }>`
  background: ${(props) => props.gradient};
  padding: 32px;
  position: relative;
  overflow: hidden;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  border: 1px solid rgba(255, 255, 255, 0.3);

  &:hover {
    transform: translateY(-8px) scale(1.02);
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.2);
  }

  &::before {
    content: '';
    position: absolute;
    top: -50%;
    right: -50%;
    width: 200px;
    height: 200px;
    background: rgba(255, 255, 255, 0.15);
    border-radius: 50%;
    transition: all 0.6s ease;
  }

  &:hover::before {
    transform: scale(1.5);
    opacity: 0.8;
  }

  &::after {
    content: '';
    position: absolute;
    bottom: -30px;
    left: -30px;
    width: 120px;
    height: 120px;
    background: rgba(255, 255, 255, 0.08);
    border-radius: 50%;
  }
`;

const StatIcon = styled.div`
  font-size: 3rem;
  margin-bottom: 20px;
  filter: drop-shadow(0 4px 12px rgba(0, 0, 0, 0.2));
  position: relative;
  z-index: 2;
  display: inline-block;
`;

const StatLabel = styled.div`
  font-size: 0.8125rem;
  color: #1f2937;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 1.2px;
  margin-bottom: 12px;
  position: relative;
  z-index: 2;
`;

const StatValue = styled.div`
  font-size: 3.5rem;
  font-weight: 900;
  color: #111827;
  line-height: 1;
  position: relative;
  z-index: 2;
  font-family: 'Inter', sans-serif;
  letter-spacing: -2px;
`;

const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 24px;

  @media (max-width: 1200px) {
    grid-template-columns: 1fr;
  }
`;

const Card = styled(GlassCard)`
  padding: 32px;
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 24px;
`;

const CardTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 700;
  color: #1f2937;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 12px;
`;

const CardIcon = styled.span`
  font-size: 1.5rem;
`;

const ChartContainer = styled.div`
  position: relative;
  height: 320px;
  margin-top: 16px;
`;

const UsersList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const UserItem = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 16px;
  background: linear-gradient(135deg, #f6f8fb 0%, #f0f2f5 100%);
  border-radius: 16px;
  transition: all 0.3s ease;
  cursor: pointer;
  border: 2px solid transparent;

  &:hover {
    background: linear-gradient(135deg, #e8ecf4 0%, #e0e4e9 100%);
    border-color: #667eea;
    transform: translateX(4px);
  }
`;

const UserAvatar = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: 700;
  font-size: 1.125rem;
  flex-shrink: 0;
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
`;

const UserInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const UserName = styled.div`
  font-weight: 700;
  color: #1f2937;
  font-size: 1rem;
  margin-bottom: 4px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const UserDate = styled.div`
  font-size: 0.8125rem;
  color: #9ca3af;
  display: flex;
  align-items: center;
  gap: 4px;
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 20px;
  gap: 20px;
`;

const Spinner = styled.div`
  width: 50px;
  height: 50px;
  border: 5px solid #f3f4f6;
  border-top-color: #667eea;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const LoadingText = styled.div`
  font-size: 1.0625rem;
  color: #6b7280;
  font-weight: 500;
`;

const ErrorContainer = styled.div`
  background: linear-gradient(135deg, #fee2e2 0%, #fecaca 100%);
  color: #991b1b;
  padding: 24px 28px;
  border-radius: 16px;
  border: 2px solid #ef4444;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 16px;
  box-shadow: 0 4px 16px rgba(239, 68, 68, 0.2);
`;

const ErrorIcon = styled.div`
  font-size: 2rem;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: #9ca3af;
`;

const EmptyIcon = styled.div`
  font-size: 4rem;
  margin-bottom: 16px;
  opacity: 0.5;
`;

const EmptyText = styled.div`
  font-size: 1rem;
  font-weight: 500;
`;

const DashboardStats: React.FC = () => {
  const chartRef = useRef<HTMLCanvasElement>(null);
  const chartInstanceRef = useRef<Chart | null>(null);

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['dashboardStats'],
    queryFn: adminApi.getDashboardStats,
  });

  useEffect(() => {
    if (data && chartRef.current) {
      if (chartInstanceRef.current) {
        chartInstanceRef.current.destroy();
      }

      const ctx = chartRef.current.getContext('2d');
      if (ctx) {
        const gradient = ctx.createLinearGradient(0, 0, 0, 300);
        gradient.addColorStop(0, 'rgba(102, 126, 234, 0.3)');
        gradient.addColorStop(1, 'rgba(102, 126, 234, 0.05)');

        chartInstanceRef.current = new Chart(ctx, {
          type: 'line',
          data: {
            labels: data.message_activity_chart_labels || [],
            datasets: [
              {
                label: 'Sporočila',
                data: data.message_activity_chart_data || [],
                borderColor: '#667eea',
                backgroundColor: gradient,
                borderWidth: 3,
                fill: true,
                tension: 0.4,
                pointRadius: 6,
                pointHoverRadius: 8,
                pointBackgroundColor: '#667eea',
                pointBorderColor: '#fff',
                pointBorderWidth: 3,
                pointHoverBackgroundColor: '#667eea',
                pointHoverBorderColor: '#fff',
                pointHoverBorderWidth: 4,
              },
            ],
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              legend: { display: false },
              tooltip: {
                backgroundColor: '#1f2937',
                padding: 16,
                cornerRadius: 12,
                titleFont: { size: 14, weight: 'bold' },
                bodyFont: { size: 16, weight: 'bold' },
                displayColors: false,
                callbacks: {
                  label: (context) => `${context.parsed.y} sporočil`,
                },
              },
            },
            scales: {
              y: {
                beginAtZero: true,
                ticks: {
                  color: '#6b7280',
                  font: { size: 13 },
                  precision: 0,
                  padding: 10,
                },
                grid: {
                  color: '#f3f4f6',
                },
                border: {
                  display: false,
                },
              },
              x: {
                ticks: {
                  color: '#6b7280',
                  font: { size: 13 },
                  padding: 10,
                },
                grid: {
                  display: false,
                },
                border: {
                  display: false,
                },
              },
            },
            interaction: {
              intersect: false,
              mode: 'index',
            },
          },
        });
      }
    }

    return () => {
      if (chartInstanceRef.current) {
        chartInstanceRef.current.destroy();
      }
    };
  }, [data]);

  if (isLoading) {
    return (
      <LoadingContainer>
        <Spinner />
        <LoadingText>Nalaganje podatkov...</LoadingText>
      </LoadingContainer>
    );
  }

  if (isError) {
    return (
      <ErrorContainer>
        <ErrorIcon>⚠️</ErrorIcon>
        <div>
          <strong>Napaka pri nalaganju podatkov</strong>
          <div style={{ fontSize: '0.9375rem', marginTop: '4px', opacity: 0.9 }}>
            {error?.message || 'Prosimo, poskusite znova kasneje.'}
          </div>
        </div>
      </ErrorContainer>
    );
  }

  const stats = [
    {
      icon: '👥',
      label: 'Skupaj Uporabnikov',
      value: data?.total_users || 0,
      gradient: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
    },
    {
      icon: '✨',
      label: 'Novi (7 dni)',
      value: data?.new_users_7_days || 0,
      gradient: 'linear-gradient(135deg, #ec4899 0%, #f43f5e 100%)',
    },
    {
      icon: '💬',
      label: 'Skupaj Sej',
      value: data?.total_sessions || 0,
      gradient: 'linear-gradient(135deg, #06b6d4 0%, #3b82f6 100%)',
    },
    {
      icon: '📨',
      label: 'Skupaj Sporočil',
      value: data?.total_messages || 0,
      gradient: 'linear-gradient(135deg, #10b981 0%, #14b8a6 100%)',
    },
  ];

  return (
    <Container>
      <StatsGrid>
        {stats.map((stat, index) => (
          <StatCard key={index} gradient={stat.gradient}>
            <StatIcon>{stat.icon}</StatIcon>
            <StatLabel>{stat.label}</StatLabel>
            <StatValue>{stat.value.toLocaleString('sl-SI')}</StatValue>
          </StatCard>
        ))}
      </StatsGrid>

      <ContentGrid>
        <Card>
          <CardHeader>
            <CardTitle>
              <CardIcon>📈</CardIcon>
              Aktivnost Sporočil
            </CardTitle>
          </CardHeader>
          <ChartContainer>
            <canvas ref={chartRef} />
          </ChartContainer>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>
              <CardIcon>🆕</CardIcon>
              Zadnji Uporabniki
            </CardTitle>
          </CardHeader>
          {data?.latest_users && data.latest_users.length > 0 ? (
            <UsersList>
              {data.latest_users.map((user: any) => (
                <UserItem key={user.id}>
                  <UserAvatar>{user.username.charAt(0).toUpperCase()}</UserAvatar>
                  <UserInfo>
                    <UserName>{user.username}</UserName>
                    <UserDate>
                      <span>📅</span>
                      {new Date(user.date_joined).toLocaleDateString('sl-SI', {
                        day: 'numeric',
                        month: 'short',
                        year: 'numeric',
                      })}
                    </UserDate>
                  </UserInfo>
                </UserItem>
              ))}
            </UsersList>
          ) : (
            <EmptyState>
              <EmptyIcon>👤</EmptyIcon>
              <EmptyText>Ni novih uporabnikov</EmptyText>
            </EmptyState>
          )}
        </Card>
      </ContentGrid>
    </Container>
  );
};

export default DashboardStats;
